import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Popover, PopoverPanel } from '@headlessui/react';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import useClickOutsideHandler from 'flow/hooks/useClickOutsideHandler';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { extractName } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import ActiveProfileStatus from 'flow/components/ui-components/ActiveProfileStatus';

// The state kept by the mention inserter.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Initial mention inserter state.
export var getInitialMentionInserterState = function getInitialMentionInserterState() {
  return {
    open: false
  };
};

// Function to calculate the mention inserter state based upon the message
// input text area.
export var getNextMentionInserterState = function getNextMentionInserterState(currentState, input) {
  // Look for an @ behind the cursor
  var message = input.value;
  var cursorPosition = input.selectionStart;
  var precedingAt = message.lastIndexOf('@', cursorPosition);
  if (precedingAt < 0) {
    // No @ behind the cursor, so can't possibly be completing a mention.
    return {
      open: false
    };
  }

  // Gather words between the @ and the cursor.
  var words = message.slice(precedingAt + 1, cursorPosition).split(/\s+/);
  if (words.length > 2) {
    // More than a first name and last name, so not a good candidate for completion.
    return {
      open: false
    };
  }

  // Candidate for the mention inserter.
  return _objectSpread(_objectSpread({}, currentState), {}, {
    open: true,
    filterWords: words,
    replaceFrom: precedingAt,
    replaceTo: cursorPosition
  });
};

// This is called when there is a keystroke in the message input, and gives the mention inserter
// chance to process or act upon it. Returns true if there should be no further processing.
export var offerKeyEventToMessageInserter = function offerKeyEventToMessageInserter(event, state, updateState, message, updateMessage) {
  // If it's not open or there's no proposed insertion, nothing to do.
  if (!state.open || !state.proposedInsertion) {
    return false;
  }

  // Otherwise, go by key.
  switch (event.key) {
    case 'Enter':
      {
        // Insert the proposed mention and close; don't propagate the event.
        insertMention(state, updateState, message, updateMessage);
        event.preventDefault();
        return true;
      }
    case 'Escape':
      {
        // Close the mention inserter.
        updateState({
          open: false
        });
        return true;
      }
    case 'ArrowUp':
      {
        updateState(_objectSpread(_objectSpread({}, state), {}, {
          insertionOffset: state.insertionOffset - 1
        }));
        event.preventDefault();
        return true;
      }
    case 'ArrowDown':
      {
        updateState(_objectSpread(_objectSpread({}, state), {}, {
          insertionOffset: state.insertionOffset + 1
        }));
        event.preventDefault();
        return true;
      }
  }
  return false;
};
var insertMention = function insertMention(state, updateState, message, updateMessage) {
  if (!state.open) {
    return;
  }
  var before = message.substring(0, state.replaceFrom);
  var toInsert = '@' + state.proposedInsertion + ' ';
  updateMessage(before + toInsert + message.substring(state.replaceTo), before.length + toInsert.length);
  updateState({
    open: false
  });
};
// Component is a popover showing the mentions we could potentially insert.
export var MentionInserter = function MentionInserter(_ref) {
  var state = _ref.state,
    updateState = _ref.updateState,
    message = _ref.message,
    updateMessage = _ref.updateMessage,
    sessionKey = _ref.sessionKey;
  // Set of all mentionables are those in the current chat.
  var mentionables = useAppSelector(function (state) {
    var _state$chats$getIn;
    var allExtensions = state.extensions.get('extensions');
    if (!allExtensions) {
      return undefined;
    }
    return (_state$chats$getIn = state.chats.getIn(['sessions', sessionKey, 'members'])) === null || _state$chats$getIn === void 0 ? void 0 : _state$chats$getIn.map(function (m) {
      return allExtensions.get(m.get('extensionKey'));
    });
  });

  // Decide what mentionables are applicable.
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var filteredMentionables = useMemo(function () {
    var _mentionables$filter;
    return mentionables === null || mentionables === void 0 || (_mentionables$filter = mentionables.filter(function (mentionable) {
      // If we're not open, no mentionables make it through.
      if (!state.open) {
        return false;
      }

      // If the mentionable is us, or lacks contact information, it's never
      // applicable.
      if (!mentionable || mentionable.get('key') === ownExtensionKey) {
        return false;
      }
      var name = extractName(mentionable.get('contact'), '');
      if (!name) {
        return false;
      }

      // Filter, by both names if two words, or either name if one word.
      var nameParts = name.split(' ');
      switch (state.filterWords.length) {
        case 1:
          return nameParts.some(function (p) {
            return p.toLowerCase().startsWith(state.filterWords[0].toLowerCase());
          });
        case 2:
          return (nameParts[0] || '').toLowerCase().startsWith(state.filterWords[0].toLowerCase()) && (nameParts[1] || '').toLowerCase().startsWith(state.filterWords[1].toLowerCase());
        default:
          return true;
      }
    })) === null || _mentionables$filter === void 0 ? void 0 : _mentionables$filter.sort(sortByName);
  }, [mentionables, state.open, state.open ? state.filterWords.join(' ') : '']);

  // The displayed mentions (accounting for arrow key handling), as well as the current offset
  // of the mentions we show (to give nicer UX when we use arrow keys by minimizing the amount
  // of scrolling we do).
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    displayedMentionables = _useState2[0],
    setDisplayedMentionables = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    offsetStart = _useState4[0],
    setOffsetStart = _useState4[1];
  var maxMentions = 5;
  useEffect(function () {
    // If no mentions to show, don't go any further, and make sure we're in closed state.
    if (!state.open || !filteredMentionables || filteredMentionables.size === 0) {
      setDisplayedMentionables(undefined);
      setOffsetStart(0);
      updateState({
        open: false
      });
      return;
    }

    // Make sure insertion offset is in range; reset to 0 if not (or if unset), and determine
    // correct insertion candidate. Update state if needed.
    var newInsertionOffset = Math.min(Math.max(state.insertionOffset || 0, 0), filteredMentionables.size - 1);
    var newProposedInsertion = extractName(filteredMentionables.getIn([newInsertionOffset, 'contact']), '');
    if (state.insertionOffset !== newInsertionOffset || state.proposedInsertion !== newProposedInsertion) {
      updateState(_objectSpread(_objectSpread({}, state), {}, {
        insertionOffset: newInsertionOffset,
        proposedInsertion: newProposedInsertion
      }));
    }

    // Make sure the offset start doesn't result in us showing less than the
    // desired number of mentions, and that the mention we are currently on
    // is in the window.
    var newOffsetStart = offsetStart;
    if (filteredMentionables.size - offsetStart < maxMentions) {
      newOffsetStart = 0;
    }
    if (newInsertionOffset < offsetStart) {
      // We're too far down the list; move up.
      newOffsetStart = newInsertionOffset;
    } else if (newInsertionOffset >= offsetStart + maxMentions) {
      // We're too far up the list; move down.
      newOffsetStart = newInsertionOffset - maxMentions + 1;
    }
    setDisplayedMentionables(filteredMentionables.slice(newOffsetStart, newOffsetStart + maxMentions));
    if (newOffsetStart !== offsetStart) {
      setOffsetStart(newOffsetStart);
    }
  }, [(filteredMentionables === null || filteredMentionables === void 0 ? void 0 : filteredMentionables.size) || 0, state.open ? state.insertionOffset : -1, offsetStart]);
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var statuses = useAppSelector(function (state) {
    return state.extensions.get('statuses');
  });
  var popoverRef = useClickOutsideHandler(function () {
    return updateState({
      open: false
    });
  });
  return /*#__PURE__*/_jsx(Popover, {
    children: state.open && displayedMentionables && /*#__PURE__*/_jsx(PopoverPanel, {
      "static": true,
      className: "absolute top-0 z-40 w-[360px] -translate-y-full rounded-sm bg-white p-2 shadow-lg outline-hidden",
      ref: popoverRef,
      children: /*#__PURE__*/_jsx("div", {
        className: "flex flex-col",
        children: displayedMentionables.map(function (mentionable, index) {
          var contact = mentionable.get('contact');
          var name = extractName(contact, i18next.t('Contact.Unknown'));
          var active = state.open && name === state.proposedInsertion;
          return /*#__PURE__*/_jsxs("div", {
            className: twMerge('group flex h-12 w-full cursor-pointer items-center gap-x-4 rounded-sm p-2', active ? 'bg-gray-200' : ''),
            onMouseEnter: function onMouseEnter() {
              return updateState(_objectSpread(_objectSpread({}, state), {}, {
                insertionOffset: offsetStart + index
              }));
            },
            onWheel: function onWheel(e) {
              var notches = Math.ceil(Math.abs(e.deltaY / 120)) * (e.deltaY < 0 ? -1 : 1);
              var maxOffsetStart = Math.max(filteredMentionables.size - maxMentions, 0);
              setOffsetStart(Math.min(Math.max(offsetStart + notches, 0), maxOffsetStart));
            },
            onClick: function onClick() {
              return insertMention(_objectSpread(_objectSpread({}, state), {}, {
                proposedInsertion: name
              }), updateState, message, updateMessage);
            },
            children: [/*#__PURE__*/_jsx(ContactAvatar, {
              contact: contact,
              size: "small",
              status: statuses.get(mentionable.get('key')) || 'unknown'
            }), /*#__PURE__*/_jsxs("div", {
              className: "flex w-[300px] flex-col",
              children: [/*#__PURE__*/_jsx("p", {
                className: "w-full truncate overflow-hidden text-base/4 whitespace-nowrap",
                children: name
              }), active && /*#__PURE__*/_jsx("p", {
                className: "w-full truncate overflow-hidden text-sm/4 whitespace-nowrap text-gray-500",
                children: /*#__PURE__*/_jsx(ActiveProfileStatus, {
                  extensionKey: mentionable === null || mentionable === void 0 ? void 0 : mentionable.get('key')
                })
              })]
            })]
          }, mentionable.get('chatUserKey'));
        })
      })
    })
  });
};
var sortByName = function sortByName(a, b) {
  var aName = extractName(a.get('contact'));
  var bName = extractName(b.get('contact'));
  return aName < bName ? -1 : 1;
};